import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Upload,
  Row,
  Col,
  Spin,
  Card,
  Avatar,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchCountries as apiFetchCountries,
  fetchStates as apiFetchStates,
  fetchCities as apiFetchCities,
  fetchFirms as apiFetchFirms,
  createLawyer as apiCreateLawyer,
} from "../../../api/lawyerApi";

export default function CreateLawyerModal({ open, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [firms, setFirms] = useState([]);

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const fetchCountries = async () => {
    try {
      const res = await apiFetchCountries();
      setCountries(res);
    } catch {
      toastError("Failed to load countries");
    }
  };

  const fetchStates = async (countryId) => {
    setLoadingStates(true);
    try {
      const res = await apiFetchStates(countryId);
      setStates(res);
      setCities([]);
      form.setFieldsValue({ state_id: null, city_id: null });
    } catch {
      toastError("Failed to load states");
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    setLoadingCities(true);
    try {
      const res = await apiFetchCities(stateId);
      setCities(res);
      form.setFieldsValue({ city_id: null });
    } catch {
      toastError("Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const res = await apiFetchFirms();
      setFirms(res);
    } catch {
      toastError("Failed to fetch firms");
    }
  };

  useEffect(() => {
    if (open) {
      fetchCountries();
      fetchFirms();
      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
    }
  }, [open]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => {
        if (dayjs.isDayjs(val)) {
          formData.append(key, val.format("YYYY-MM-DD"));
        } else {
          formData.append(key, val);
        }
      });

      formData.append("role", "lawyer");
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await apiCreateLawyer(formData);
      toastSuccess("Lawyer created successfully");
      form.resetFields();
      onClose();
      onSuccess();
    } catch (error) {
      console.error(error);
      toastError("Failed to create lawyer");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Modal
      title={<span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Create New Lawyer</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      style={{ top: 20 }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Card
          title="Basic Information"
          bordered={false}
          headStyle={{ border: 'none', padding: '0 0 16px 0' }}
          bodyStyle={{ padding: '16px 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Upload.Dragger
                  name="image"
                  beforeUpload={handleImageChange}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  style={{ width: '100%', marginBottom: 16 }}
                >
                  {imagePreview ? (
                    <Avatar
                      src={imagePreview}
                      size={120}
                      style={{ margin: '0 auto' }}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>
                      <p className="ant-upload-drag-icon">
                        <UserOutlined style={{ fontSize: '32px' }} />
                      </p>
                      <p className="ant-upload-text">Upload Profile Photo</p>
                    </div>
                  )}
                </Upload.Dragger>
              </div>
            </Col>
            <Col span={16}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Full Name"
                    name="name"
                    rules={[{ required: true, message: 'Please input full name' }]}
                  >
                    <Input placeholder="Full name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please input email' },
                      { type: "email", message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input placeholder="Email address" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[{ required: true, message: 'Please input password' }]}
                  >
                    <Input.Password placeholder="Password" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Mobile"
                    name="mobile"
                    rules={[{ required: true, message: 'Please input mobile number' }]}
                  >
                    <Input placeholder="Mobile number" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        <Card
          title="Personal Details"
          bordered={false}
          headStyle={{ border: 'none', padding: '16px 0' }}
          bodyStyle={{ padding: '16px 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Date of Birth" name="date_of_birth">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Gender" name="gender" rules={[{ required: true }]}>
                <Select
                  placeholder="Select gender"
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                    { value: "other", label: "Other" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="CNIC No" name="cnic_no">
                <Input placeholder="CNIC number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <Input.TextArea rows={2} placeholder="Full address" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Location Information"
          bordered={false}
          headStyle={{ border: 'none', padding: '16px 0' }}
          bodyStyle={{ padding: '16px 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                label="Country"
                name="country_id"
                rules={[{ required: true, message: "Please select a country" }]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Country"
                  onChange={(value) => fetchStates(value)}
                  options={countries.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="State" name="state_id" rules={[{ required: true }]}>
                <Spin spinning={loadingStates}>
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select State"
                    onChange={(value) => {
                      form.setFieldsValue({ state_id: value, city_id: null }); // ✅ update form state
                      fetchCities(value);
                    }}
                    value={form.getFieldValue("state_id")} // ✅ bind value from form
                    options={states.map((s) => ({
                      label: s.name,
                      value: s.id,
                    }))}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Spin>
              </Form.Item>

            </Col>
            <Col span={8}>
              <Form.Item
                label="City"
                name="city_id"
                rules={[{ required: true, message: 'Please select a city' }]}
              >
                <Spin spinning={loadingCities}>
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select City"
                    value={form.getFieldValue("city_id")?.toString()} // ✅ string value
                    onChange={(value) => {
                      form.setFieldsValue({ city_id: value }); // ✅ update form value
                    }}
                    options={cities.map((c) => ({
                      label: c.name,
                      value: c.id.toString(), // ✅ string option value
                    }))}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Spin>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Postal Code" name="postal_code">
                <Input placeholder="Postal code" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                hidden
                label="Firm"
                name="firm_id"
                initialValue={1} // ✅ This will set the value to 1
              >
                <Input placeholder="Firm" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Professional Information"
          bordered={false}
          headStyle={{ border: 'none', padding: '16px 0' }}
          bodyStyle={{ padding: '16px 0 0 0' }}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="License Number" name="license_number">
                <Input placeholder="License number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Specialization" name="specialization">
                <Input placeholder="Area of specialization" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Status" name="status" rules={[{ required: true }]}>
                <Select
                  placeholder="Select status"
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="License Issue Date" name="license_issue_date">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="License Expiry Date" name="license_expiry_date">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            style={{ marginRight: 16, minWidth: 100 }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ minWidth: 120 }}
          >
            Create Lawyer
          </Button>
        </div>
      </Form>
    </Modal>
  );
}